import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Apps as AppsIcon,
  AddBusiness,
  AddCircle,
  People as PeopleIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../utils/constants';

// Shared width constants — also consumed by BusinessLayout/AdminLayout so
// the main content area can offset itself to match the drawer's width.
export const DRAWER_WIDTH = 240;
export const COLLAPSED_DRAWER_WIDTH = 72;

/**
 * desktopOpen — on desktop (permanent drawer): true = full width,
 *               false = collapsed to an icon-only rail.
 * mobileOpen  — on mobile (temporary drawer): whether the overlay is shown.
 * onMobileClose — called to hide the mobile overlay (backdrop click, nav, etc).
 */
const Sidebar = ({ desktopOpen, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const adminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: ROUTES.ADMIN_DASHBOARD },
    { text: 'Businesses', icon: <BusinessIcon />, path: ROUTES.ADMIN_BUSINESSES },
    { text: 'Create Business', icon: <AddBusiness />, path: ROUTES.ADMIN_CREATE_BUSINESS },
    { text: 'Create Application', icon: <AddCircle />, path: ROUTES.ADMIN_CREATE_APPLICATION },
    { text: 'Applications', icon: <AppsIcon />, path: ROUTES.ADMIN_APPLICATIONS },
    { text: 'Subscribers', icon: <PeopleIcon />, path: '/admin/subscribers' },
    { text: 'AuditLogs', icon: <HistoryIcon />, path: ROUTES.ADMIN_LOGS },
  ];

  const businessMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: ROUTES.BUSINESS_DASHBOARD },
    { text: 'Applications', icon: <AppsIcon />, path: ROUTES.BUSINESS_APPLICATIONS },
    { text: 'Subscribers', icon: <PeopleIcon />, path: '/business/subscribers' },
    { text: 'Audit Logs', icon: <HistoryIcon />, path: '/business/audit-logs' },
  ];

  const menuItems = isAdmin ? adminMenuItems : businessMenuItems;

  // On desktop the drawer is always mounted (permanent) — desktopOpen just
  // controls whether it's the full width or the collapsed icon rail.
  const collapsed = isDesktop && !desktopOpen;

  const handleNavigation = (path) => {
    navigate(path);
    if (!isDesktop) onMobileClose();
  };

  const isActive = (path) => location.pathname === path;

  const currentWidth = isDesktop ? (desktopOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH) : DRAWER_WIDTH;

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={isDesktop ? true : mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isDesktop ? currentWidth : 0,
        flexShrink: 0,
        transition: (t) => t.transitions.create('width', {
          easing: t.transitions.easing.sharp,
          duration: t.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: currentWidth,
          overflowX: 'hidden',
          marginTop: isDesktop ? '72px' : 0,
          height: isDesktop ? 'calc(100% - 72px)' : '100%',
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          transition: (t) => t.transitions.create('width', {
            easing: t.transitions.easing.sharp,
            duration: t.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* SECTION HEADER */}
        {!collapsed && (
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: 'text.secondary',
                textTransform: 'uppercase',
              }}
            >
              Navigation
            </Typography>
          </Box>
        )}
        {collapsed && <Box sx={{ pt: 3 }} />}

        <List sx={{ px: 1 }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);

            const button = (
              <ListItemButton
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                selected={active}
                sx={{
                  mb: 0.5,
                  borderRadius: 1.5,
                  px: collapsed ? 0 : 2,
                  py: 1.2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  color: active ? 'primary.main' : 'text.primary',
                  backgroundColor: active ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: active ? 'rgba(37, 99, 235, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'inherit', justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                )}
              </ListItemButton>
            );

            return collapsed ? (
              <Tooltip key={item.text} title={item.text} placement="right">
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}
        </List>

        <Divider sx={{ my: 2, mx: collapsed ? 1 : 2 }} />

        {!collapsed && (
          <Box sx={{ mt: 'auto', px: 3, pb: 2 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center' }}>
              © OK200 Admin
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
